import { useEffect, useRef, useState } from 'react';
import { Upload, Button, useApp, Image, Space, Tag, Tooltip, theme } from '../ui';
import { Upload as UploadIcon, Trash2, Star } from 'lucide-react';
import type { UploadProps } from '../ui';
import { api } from '../api/client';
import { toImageUrls } from '../lib/imageUrls';

interface Props {
  /** Gallery URLs — first one is the cover. Bound via Form.Item value/onChange. */
  value?: string[];
  onChange?: (urls: string[]) => void;
  max?: number;
  disabled?: boolean;
}

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';
const MAX_MB = 5;

export function MultiImageUploadField({ value, onChange, max = 8, disabled }: Props) {
  const { message } = useApp();
  const { token } = theme.useToken();
  const [pending, setPending] = useState(0);
  // Normalise in case the parent hands us a JSON-encoded array string rather than
  // a real array — otherwise `.map`/`.length` below would walk the characters.
  const urls = toImageUrls(value);
  const busy = pending > 0;

  // Authoritative copy of the gallery for appends. Selecting multiple files fires
  // beforeUpload for each in parallel; without a single source of truth + serialized
  // appends, every handler would read the same stale `value` and clobber the others
  // (only the last upload would survive). The ref + queue below fix that.
  const urlsRef = useRef<string[]>(urls);
  useEffect(() => { urlsRef.current = toImageUrls(value); }, [value]);
  const queue = useRef<Promise<void>>(Promise.resolve());

  const uploadProps: UploadProps = {
    accept: ACCEPT,
    showUploadList: false,
    multiple: true,
    disabled: disabled || busy,
    beforeUpload: (file) => {
      setPending((p) => p + 1);
      // Chain each file so appends happen one at a time against the freshest list.
      queue.current = queue.current.then(async () => {
        try {
          if (urlsRef.current.length >= max) {
            message.warning(`You can add up to ${max} images`);
            return;
          }
          if (file.size > MAX_MB * 1024 * 1024) {
            message.error(`"${file.name}" is too large — max ${MAX_MB} MB`);
            return;
          }
          const form = new FormData();
          form.append('file', file);
          const r = await api.post('/uploads/product-image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
          const url = (r.data?.data ?? r.data)?.url;
          if (!url) throw new Error('Server did not return a URL');
          const next = [...urlsRef.current, url];
          urlsRef.current = next;
          onChange?.(next);
        } catch (e: any) {
          message.error(e.response?.data?.error?.message ?? e.message ?? 'Upload failed');
        } finally {
          setPending((p) => p - 1);
        }
      });
      return Upload.LIST_IGNORE;
    },
  };

  const removeAt = (i: number) => {
    const next = urls.filter((_, idx) => idx !== i);
    urlsRef.current = next;
    onChange?.(next);
  };
  const makeCover = (i: number) => {
    if (i === 0) return;
    const arr = [...urls];
    const [picked] = arr.splice(i, 1);
    const next = [picked, ...arr];
    urlsRef.current = next;
    onChange?.(next);
  };

  const thumb = (u: string, i: number) => {
    const isCover = i === 0;
    return (
      <div
        key={`${u}-${i}`}
        style={{
          position: 'relative',
          border: `${isCover ? 2 : 1}px solid ${isCover ? token.colorPrimary : token.colorBorderSecondary}`,
          borderRadius: 8,
          padding: 4,
        }}
      >
        <Image src={u} width={84} height={84} style={{ objectFit: 'cover', borderRadius: 6 }} preview={{ src: u }} />
        {isCover ? (
          <Tag tone="green" style={{ position: 'absolute', bottom: 4, left: 4, marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
            <Star size={12} style={{ marginRight: 2 }} />Cover
          </Tag>
        ) : (
          <Tooltip title="Set as cover">
            <Button
              size="sm"
              icon={<Star size={16} />}
              onClick={() => makeCover(i)}
              disabled={disabled}
              style={{ position: 'absolute', bottom: 4, left: 4, fontSize: 10, height: 20, padding: '0 6px', lineHeight: '18px' }}
            >
              Cover
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Remove">
          <Button
            size="sm"
            shape="circle"
            variant="danger"
            aria-label="Remove"
            icon={<Trash2 size={16} />}
            onClick={() => removeAt(i)}
            disabled={disabled}
            style={{ position: 'absolute', top: -8, right: -8 }}
          />
        </Tooltip>
      </div>
    );
  };

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      <Space wrap>
        <Upload {...uploadProps}>
          <Button icon={<UploadIcon size={16} />} loading={busy} disabled={disabled || urls.length >= max}>
            Upload images
          </Button>
        </Upload>
        <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>
          <Star size={16} style={{ color: token.colorWarning }} /> tap “Cover” on any photo to make it the thumbnail · {urls.length}/{max}
        </span>
      </Space>
      {urls.length > 0 && <Space wrap size={12}>{urls.map(thumb)}</Space>}
    </Space>
  );
}
